import { isFuture } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { AutocompleteType } from "../../../common/types/autoComplete";
import {
  CANCEL_ASSIGNMENT_OPTIONS,
  CANCEL_LONG_ORDER_OPTIONS,
  CANCEL_SHORT_ORDER_OPTIONS,
  CancelAssignmentScope,
  OrderDialogMode,
} from "../constants";
import { DisassembleBundleDialog } from "../dialogs/DisassembleBundleDialog";
import { OrderCancellationDialog } from "../dialogs/OrderCancellationDialog";
import { OrderSaveDialog } from "../dialogs/OrderSaveDialog";
import { ManualOfferDialog } from "../dialogs/manualOfferDialog/ManualOfferDialog";
import { OrderEdit, OrderType } from "../orderAPI";
import { ShiftSummary } from "../types/editOrder";
import { getEarliestShiftTime, isBundleOrder } from "../utils";
import { AutomatchingStatus } from "../orderTypes";

type Props = {
  orderRow: OrderEdit;
  openMode: OrderDialogMode;
  cancelOrderRows: (reasonForCancellation: string) => void;
  cancelAssignment: (
    reasonForCancellation: string,
    replaceTalent?: boolean,
    setAutomatchingStatus?: AutomatchingStatus,
  ) => void;
  cancelAssignmentScope: CancelAssignmentScope;
  cancelledShifts: ShiftSummary[];
  cancelAssignmentPaySalary?: boolean;
  onSaveOrderRows: (editReason: string) => void;
  isLoading: boolean;
  closeDialog: () => void;
  costCenters: AutocompleteType[];
  removeShiftsFromBundle: (
    shiftsIds: { id: string; version: number }[],
  ) => void;
  isAutomatchingOn?: boolean;
};

// FIXME refactor into a smarter solution?
export const EditOrderDialogs = ({
  openMode,
  cancelOrderRows,
  cancelAssignment,
  cancelAssignmentScope,
  cancelledShifts,
  cancelAssignmentPaySalary,
  onSaveOrderRows,
  isLoading,
  closeDialog,
  costCenters,
  removeShiftsFromBundle,
  orderRow,
  isAutomatchingOn,
}: Props) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelAssignmentDialogOpen, setIsCancelAssignmentDialogOpen] =
    useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isDisassembleBundleDialogOpen, setIsDisassembleBundleDialogOpen] =
    useState(false);

  useEffect(() => {
    switch (openMode) {
      case OrderDialogMode.CancelRows:
        setIsCancelDialogOpen(true);
        break;
      case OrderDialogMode.CancelAssignment:
        setIsCancelAssignmentDialogOpen(true);
        break;
      case OrderDialogMode.Edit:
        setIsEditDialogOpen(true);
        break;
      case OrderDialogMode.Search:
        setIsSearchDialogOpen(true);
        break;
      case OrderDialogMode.Disassemble:
        setIsDisassembleBundleDialogOpen(true);
        break;
      default:
        setIsCancelDialogOpen(false);
        setIsCancelAssignmentDialogOpen(false);
        setIsEditDialogOpen(false);
        setIsSearchDialogOpen(false);
        setIsDisassembleBundleDialogOpen(false);
        break;
    }
  }, [openMode]);

  const shifts = orderRow.orderRows.map((row) => ({
    id: row.id,
    version: row.version,
    details: row.shiftDetail,
  }));

  const firstShiftStart = useMemo(
    () => getEarliestShiftTime(orderRow.shiftDetails),
    [orderRow.shiftDetails],
  );

  const isFutureShift = useMemo(() => {
    // For bundle cancellations, use the earliest cancelled shift
    if (cancelAssignmentScope === CancelAssignmentScope.Bundle) {
      const bundleShiftStart = cancelledShifts.length
        ? Math.min(...cancelledShifts.map((shift) => shift.start))
        : null;
      return bundleShiftStart ? isFuture(new Date(bundleShiftStart)) : false;
    }

    // otherwise use the earliest shift in the order
    return firstShiftStart ? isFuture(new Date(firstShiftStart)) : false;
  }, [cancelAssignmentScope, cancelledShifts, firstShiftStart]);

  const cancelOrderOptions = useMemo(() => {
    return orderRow.type === OrderType.Short
      ? CANCEL_SHORT_ORDER_OPTIONS
      : CANCEL_LONG_ORDER_OPTIONS;
  }, [orderRow.type]);

  const isBundleOrderValue = isBundleOrder(orderRow.shiftDetails);

  return (
    <>
      <OrderCancellationDialog
        isOpen={isCancelDialogOpen}
        closeDialog={closeDialog}
        onCancel={cancelOrderRows}
        cancelOptions={cancelOrderOptions}
        isLoading={isLoading}
        openMode={openMode}
        isBundleOrder={isBundleOrderValue}
      />
      <OrderSaveDialog
        isOpen={isEditDialogOpen}
        closeDialog={closeDialog}
        finishSave={onSaveOrderRows}
        isLoading={isLoading}
      />
      <ManualOfferDialog
        isOpen={isSearchDialogOpen}
        closeDialog={closeDialog}
        orderRow={orderRow}
      />
      <OrderCancellationDialog
        isOpen={isCancelAssignmentDialogOpen}
        closeDialog={closeDialog}
        onCancel={cancelAssignment}
        cancelOptions={CANCEL_ASSIGNMENT_OPTIONS}
        isLoading={isLoading}
        openMode={openMode}
        isFutureShift={isFutureShift}
        cancelAssignmentScope={cancelAssignmentScope}
        cancelledShifts={cancelledShifts}
        cancelAssignmentPaySalary={cancelAssignmentPaySalary}
        isBundleOrder={isBundleOrderValue}
        isAutomatchingOn={isAutomatchingOn}
      />
      <DisassembleBundleDialog
        key={Date.now()}
        isOpen={isDisassembleBundleDialogOpen}
        closeDialog={closeDialog}
        shifts={shifts}
        costCenters={costCenters}
        removeShiftsFromBundle={removeShiftsFromBundle}
      />
    </>
  );
};
